import React, { useState } from "react";
import styled from "styled-components";
import { ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import type {
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from "@stripe/stripe-js";

const ButtonContainer = styled.div<{ $disabled?: boolean; $hidden?: boolean }>`
  padding: 4px 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  transition: opacity 0.2s ease;
  display: ${({ $hidden }) => ($hidden ? "none" : "block")};
`;

export interface ExpressCheckoutButtonProps {
  /**
   * Called when the user confirms payment in the wallet UI.
   * Should create a PaymentIntent (or SetupIntent for setup mode) and return the clientSecret.
   */
  onConfirm: () => Promise<string>;
  /**
   * Called after payment/setup is successfully confirmed.
   * For setup mode, receives the paymentMethodId.
   */
  onSuccess: (paymentMethodId?: string) => void;
  /**
   * Called if an error occurs during payment.
   */
  onError?: (error: Error) => void;
  /**
   * Called when wallet availability is determined.
   * True if Apple Pay, Google Pay, or other express methods are available.
   */
  onAvailabilityChange?: (available: boolean) => void;
  /**
   * Whether the button should be disabled.
   */
  disabled?: boolean;
  /**
   * Set to true when saving payment method for future use (setup mode).
   * Uses confirmSetup instead of confirmPayment.
   */
  setupMode?: boolean;
}

const ExpressCheckoutButton = ({
  onConfirm,
  onSuccess,
  onError,
  onAvailabilityChange,
  disabled = false,
  setupMode = false,
}: ExpressCheckoutButtonProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isAvailable, setIsAvailable] = useState(false);

  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const { availablePaymentMethods } = event;

    // Check if any express checkout methods are available (Apple Pay or Google Pay)
    const available = !!(availablePaymentMethods?.applePay || availablePaymentMethods?.googlePay);
    setIsAvailable(available);
    onAvailabilityChange?.(available);
  };

  const handleConfirm = async (_event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      onError?.(new Error("Stripe not initialized"));
      return;
    }

    try {
      // Submit the collected payment information
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError?.(new Error(submitError.message || "Failed to submit payment details"));
        return;
      }

      // Get the clientSecret from the parent (creates PaymentIntent or SetupIntent server-side)
      const clientSecret = await onConfirm();

      if (!clientSecret) {
        onError?.(new Error(setupMode ? "Failed to create setup intent" : "Failed to create payment intent"));
        return;
      }

      if (setupMode) {
        // Setup mode: confirm the SetupIntent to save the payment method
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        if (error) {
          onError?.(new Error(error.message || "Setup confirmation failed"));
        } else if (setupIntent?.status === "succeeded") {
          // Extract paymentMethodId from the setupIntent
          const paymentMethod = setupIntent.payment_method;
          const paymentMethodId = typeof paymentMethod === "string" ? paymentMethod : paymentMethod?.id;
          onSuccess(paymentMethodId);
        } else {
          onError?.(new Error("Setup failed"));
        }
      } else {
        // Payment mode: confirm the PaymentIntent
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        if (error) {
          onError?.(new Error(error.message || "Payment confirmation failed"));
        } else if (paymentIntent?.status === "succeeded") {
          onSuccess();
        } else if (paymentIntent?.status === "requires_action") {
          // 3D Secure or other action required - Stripe handles this automatically
          // The redirect: "if_required" should handle this case
          onError?.(new Error("Additional authentication required"));
        } else {
          // Payment is processing or in another state
          onSuccess();
        }
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error("Payment failed"));
    }
  };

  const buttonType = "plain";

  // Always render the element so onReady can fire, but hide it when not available
  return (
    <ButtonContainer $disabled={disabled} $hidden={!isAvailable}>
      <ExpressCheckoutElement
        onReady={handleReady}
        onConfirm={handleConfirm}
        options={{
          buttonType: {
            applePay: buttonType,
            googlePay: buttonType,
          },
          buttonTheme: {
            applePay: "white-outline",
            googlePay: "white",
          },
          buttonHeight: 48,
          // Show all available wallet buttons without "See more" overflow
          layout: {
            maxColumns: 1,
            overflow: "never",
          },
          // Enable cross-browser wallet support:
          // - Apple Pay in Chrome/Chromium on macOS requires "always"
          // - Google Pay in Safari/Firefox requires "always"
          paymentMethods: {
            applePay: "always",
            googlePay: "always",
            link: "never",
          },
        }}
      />
    </ButtonContainer>
  );
};

export default ExpressCheckoutButton;
